// ═══════════════════════════════════════════════════════════════
// OcTriagem.jsx — aba "Triagem"
//
// A PERGUNTA QUE ORIGINOU ESTA TELA, na palavra do cliente:
//
//   "Seria possivel testarmos algum fluxo ou casos na ultima data disponivel?
//    Por exemplo, casos que nao pediram RJ/RE/Chapter11 com uma PD acima de
//    15%/20% ou aumento significativo de PD (+10 p.p. MoM)?"
//
// A aba "Movimentacao de risco" responde ESSA pergunta, com esses cortes.
// Esta aqui responde a CLASSE dela: os cortes viram parametro, e a tela passa a
// dizer POR QUE cada companhia se mexeu.
//
// ──────────────────────────────────────────────────────────────
// CINCO RECUSAS. Quatro sao herdadas da aba irma e continuam valendo aqui
// (nunca afirmar "nao pediu RJ" com o banco mudo; nunca chamar a variacao de
// mensal; nunca numerador sem denominador; nunca esconder divergencia). A
// quinta e propria desta tela e nasceu de um erro que foi para producao:
//
// 5. NUNCA deixar a lista PARECER um ranking quando ela nao e.
//
//    A PD (`pd_final`) e um ensemble de cinco lentes e METADE DO PESO vem de
//    duas que emitem FAIXA, nao valor continuo: Altman (0,30) emite 1,0% | 8,0%
//    | 35,0%; Kanitz (0,20) emite 0,5% | 3% | 15% | 50%. Consequencia medida em
//    30/06/2026: das 84 companhias com PD >= 20% e sem ato publico, 17 cabem
//    dentro de 0,28 p.p. Uma tabela ordenada por PD sugere que a primeira e
//    pior que a decima. Frequentemente nao e — e a ordem dentro do empate e
//    arbitraria.
//
//    Entao esta tela publica a granularidade do corte ANTES da tabela, marca as
//    linhas empatadas, e troca a pergunta "quem esta pior" pela pergunta que
//    tem resposta: "quem CRUZOU uma fronteira". Essa tem, e e o que o analista
//    de credito quer.
//
//    O antecedente: a PR #10 afirmou na tela que a PD saturava em 58,96% e que
//    o corte de salto excluia os piores. As duas coisas eram falsas e foram
//    mostradas ao cliente. Nenhum numero sobre o modelo e escrito a mao aqui —
//    pesos, faixas e teto vem de `d.modelo`, que o backend monta a partir de
//    services/engine/calc.js. Uma fonte, um dono.
//
// 6. NUNCA mostrar companhia fora do pacote como se fosse do cliente, NEM
//    esconder que ela existe. O escopo e servido (services/oc/
//    oc_carteira_escopo.js), nao filtrado aqui. Os controles sao os MESMOS da
//    aba irma, importados de OcEscopoControles.jsx e nao copiados: duas telas
//    com contagens diferentes na frente do mesmo cliente destroem confianca
//    mais rapido do que qualquer defeito.
// ═══════════════════════════════════════════════════════════════

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ControleEscopo, SeletorTrimestres, temSeletorDeTrimestre, Direcao, ForaDoPacote } from './OcEscopoControles';

const C = {
  navy: '#0A1628', blue: '#1A3F6F', amber: '#C87B2A', gold: '#E8C56D', cream: '#FAF7F2',
  red: '#DC2626', green: '#15803D', amber2: '#D97706', roxo: '#8067B7',
  muted: '#6b7280', line: '#e5e7eb', ink: '#0f172a', paper: '#ffffff',
};

const ratingColor = (r) => ['CC', 'C', 'D'].includes(r) ? C.red : r === 'CCC' ? C.amber
  : ['A', 'AA', 'AAA'].includes(r) ? C.green : C.blue;

const badge = {
  display: 'inline-block', padding: '2px 8px', borderRadius: 999,
  border: '1px solid', fontSize: 11.5, fontWeight: 700,
};

// pd_pct e variacao_pp ja vem do servidor em PONTOS PERCENTUAIS.
const fmtPct = (v) => v == null ? '—' : (+v).toFixed(2) + '%';
const fmtPp = (v) => v == null ? '—' : (v > 0 ? '+' : '') + (+v).toFixed(2) + ' p.p.';
const fmtData = (d) => {
  if (!d) return '—';
  const s = String(d).slice(0, 10).split('-');
  return s.length === 3 ? `${s[2]}/${s[1]}/${s[0]}` : String(d);
};

const TIPO_EVENTO = { rj_re: 'recuperação', falencia: 'falência', liquidacao: 'liquidação', chapter_11: 'Chapter 11' };

const caixa = {
  background: C.paper, border: `1px solid ${C.line}`, borderLeft: `3px solid ${C.amber}`,
  borderRadius: 10, padding: '10px 14px', fontSize: 12.5, color: C.ink,
  lineHeight: 1.55, marginBottom: 12,
};

const inp = {
  padding: '6px 9px', border: `1px solid ${C.line}`, borderRadius: 8,
  fontSize: 13, color: C.ink, background: C.paper, outline: 'none', width: 74,
};

const errBox = {
  background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b',
  borderRadius: 10, padding: '12px 14px', fontSize: 13, marginBottom: 12,
};

/* As perguntas prontas. A primeira e literalmente a do e-mail; as outras sao a
   mesma classe com outros parametros, que e o que "e outras similares" quer
   dizer. A quarta so passou a existir quando o backend comecou a devolver as
   lentes — antes dela nao havia como perguntar "quem cruzou". */
/* As perguntas prontas. Cada uma mexe SO no parametro que o nome dela menciona.
   A primeira versao definia PD e salto juntos, entao clicar em "PD >= 20%"
   mudava silenciosamente o corte de salto — o cliente notou, e tinha razao:
   botao que mexe no que nao anuncia e botao que mente.
   `foco` diz qual bloco lidera a tela; o outro continua visivel, abaixo. */
const PERGUNTAS = [
  { id: 'nivel15', rotulo: 'PD ≥ 15% sem ato público', foco: 'nivel', pd: 15, semAto: true, soVirada: false },
  { id: 'nivel20', rotulo: 'PD ≥ 20% sem ato público', foco: 'nivel', pd: 20, semAto: true, soVirada: false },
  { id: 'salto',   rotulo: 'Salto ≥ 10 p.p.',          foco: 'salto', salto: 10, semAto: true, soVirada: false },
  { id: 'virou',   rotulo: 'Mudou de faixa',           foco: 'nivel', semAto: true, soVirada: true },
];


/* ── cabecalho: o que esta sendo comparado, e com que intervalo ── */
function Cabecalho({ d }) {
  const dias = d.dias_entre_bases;
  return (
    <div style={{ ...caixa, borderLeftColor: C.navy }}>
      <div style={{ fontWeight: 700, marginBottom: 4 }}>
        Posição de {fmtData(d.data_base)}
        {d.data_base_comparacao ? <> · comparada com {fmtData(d.data_base_comparacao)}</> : <> · sem base de comparação</>}
      </div>
      {d.data_base_comparacao ? (
        <div style={{ color: C.muted }}>
          {/* RECUSA 2 (herdada): a variacao NAO e mensal. */}
          A variação é entre estas duas data-base
          {dias != null && <> — <b>{dias} dias</b> de intervalo</>}, não entre meses.
          O RiskMind lê balanço trimestral: não existe leitura mensal.
        </div>
      ) : (
        <div style={{ color: C.muted }}>
          Sem safra anterior que atenda ao piso de população: os cortes de nível valem,
          o salto e as viradas de faixa ficam indisponíveis. {d.motivo_comparacao}
        </div>
      )}
      <div style={{ color: C.muted, marginTop: 4 }}>
        {/* RECUSA 3 (herdada): numerador sempre com denominador. */}
        <b>{d.universo?.n ?? '—'}</b> companhias nesta data-base
        {d.universo?.n_comparavel != null && <> · {d.universo.n_comparavel} com leitura anterior</>}
      </div>
    </div>
  );
}

/* ── RECUSA 5: a granularidade vai ANTES da tabela ──
   Se a lista tem empate, dizer isso depois de o cliente ter priorizado por ela
   nao adianta nada. */
function Granularidade({ g, rotulo }) {
  if (!g || !g.n) return null;
  const temEmpate = g.em_empate > 0;
  return (
    <div style={{ ...caixa, borderLeftColor: temEmpate ? C.amber2 : C.muted, marginBottom: 10 }}>
      {temEmpate ? (
        <>
          <b>Esta lista não é um ranking.</b> As <b>{g.n}</b> companhias {rotulo} ocupam apenas{' '}
          <b>{g.valores_distintos}</b> valores distintos de PD — <b>{g.em_empate}</b> delas estão
          empatadas com pelo menos uma outra, e o maior empate reúne <b>{g.maior_empate}</b>.
          Dentro de um empate a ordem da tabela é arbitrária.
          <div style={{ marginTop: 6, color: C.muted }}>
            Para separar companhias dentro de um empate, use a coluna <i>por que mudou</i> e a
            ficha da companhia — não a posição na lista.
          </div>
        </>
      ) : (
        <>As <b>{g.n}</b> companhias {rotulo} têm <b>{g.valores_distintos}</b> valores distintos de PD:
          nenhuma empatada, a ordem desta lista separa de verdade.</>
      )}
    </div>
  );
}

/* A CAIXA QUE EXPLICAVA O ENSEMBLE FOI REMOVIDA EM 18/09/2026.
   Ela listava as cinco lentes com os pesos (Altman 0,30, Kanitz 0,20, ...).
   Decisao comercial do Alexandre: peso e calibragem propria, nao vai para o
   cliente. O que continua indo e a CONSEQUENCIA — que a lista empata, que um
   salto e travessia de faixa — porque disso o cliente precisa para ler a tela
   sem se enganar. `d.modelo` continua vindo do servidor e pode voltar a ser
   usado numa tela interna; o que nao pode e renderizar peso aqui. */

/* ── RECUSA 1 (herdada): sem a tabela de eventos, nao se afirma quem nao pediu ── */
function AvisoEventos({ eventos, universoN }) {
  if (eventos?.disponivel) {
    return (
      <div style={caixa}>
        <b>Sem registro público</b> quer dizer que a CVM não reclassificou a companhia como em
        recuperação, falência ou liquidação até esta leitura — não quer dizer ausência de aperto.
        {eventos.n_no_universo != null && (
          <> Nesta data-base, <b>{eventos.n_no_universo}</b> das {universoN} companhias já têm registro público.</>
        )}
      </div>
    );
  }
  const aindaNaoCarregou = /vazia|TABELA_VAZIA|42P01|does not exist/i.test(String(eventos?.motivo || ''));
  return (
    <div style={{ ...caixa, borderLeftColor: C.red }}>
      <b>{aindaNaoCarregou
        ? 'A lista de eventos de crédito ainda não foi carregada.'
        : 'A lista de eventos de crédito não pôde ser lida agora.'}</b>{' '}
      O filtro &ldquo;sem ato público&rdquo; está desligado, e a coluna some. Os cortes de PD
      continuam corretos — o que esta tela <i>não</i> pode afirmar, enquanto isso, é quem pediu
      e quem não pediu recuperação.
      {eventos?.motivo && <div style={{ color: C.muted, marginTop: 4, fontSize: 12 }}>Motivo: {eventos.motivo}</div>}
    </div>
  );
}

/* ── a coluna que só existe porque o backend passou a devolver as lentes ── */
function PorQue({ lentes }) {
  if (!lentes) return <span style={{ color: C.muted }}>—</span>;
  if (lentes.disponivel === false) {
    /* Ausencia nao e "nao virou". */
    return <span style={{ color: C.muted, fontSize: 11.5 }} title={lentes.motivo || ''}>não sei dizer</span>;
  }
  const v = lentes.viradas || [];
  if (!v.length) {
    const mk = lentes.merton_delta_pp;
    return (
      <span style={{ color: C.muted, fontSize: 11.5 }}
        title={'Nada mudou de faixa entre as duas data-base. O que mexeu na PD veio de leituras que variam de forma contínua, não de uma travessia.'}>
        nenhuma travessia
      </span>
    );
  }
  return (
    <span style={{ fontSize: 11.5 }}>
      {v.map((x, i) => (
        <span key={i} style={{ display: 'block', color: x.piorou ? C.red : C.green, whiteSpace: 'nowrap' }}
          title={`${x.lente}: ${x.de} → ${x.para}. O índice foi de ${x.de_indice} para ${x.para_indice}. `
            + `Uma travessia de faixa move a PD em degrau — compare com a variação medida ao lado `
            + `para ver o quanto desta mudança ela explica.`}>
          <b>{x.lente}</b>{' '}
          {x.mesma_zona
            ? <>degrau dentro de {x.para}</>
            : <>{x.de} → {x.para}</>}
        </span>
      ))}
    </span>
  );
}

function Tabela({ linhas, onOpen, mostrarEvento, empatados }) {
  if (!linhas.length) {
    return <div style={{ color: C.muted, fontSize: 13, padding: 20, textAlign: 'center' }}>nenhuma companhia neste corte.</div>;
  }
  const cols = ['empresa', 'setor', 'rating', 'PD', 'PD anterior', 'variação', 'por que mudou'];
  if (mostrarEvento) cols.push('registro público');
  return (
    <div style={{ background: C.paper, border: `1px solid ${C.line}`, borderRadius: 12, overflow: 'hidden' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ background: '#f8fafc' }}>
            {cols.map((c, i) => (
              <th key={c} style={{ textAlign: i >= 3 && i <= 5 ? 'right' : 'left', padding: '9px 12px', color: C.muted, fontWeight: 600, fontSize: 11.5, textTransform: 'uppercase', letterSpacing: 0.4, borderBottom: `1px solid ${C.line}`, whiteSpace: 'nowrap' }}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {linhas.map((r) => {
            /* RECUSA 5: a linha empatada diz que esta empatada, na propria linha. */
            /* RECUSA 6. `fora_da_carteira` vem do servidor. `empatada` e
               `naFronteira` dependem de dado que a linha mascarada nao tem —
               `empatados.has(null)` seria verdadeiro para TODAS as mascaradas
               se `null` estivesse no conjunto, e "empatada" numa linha sem PD
               nao quer dizer nada. Entao os dois so valem quando ha numero. */
            const fora = r.fora_da_carteira === true;
            const empatada = !fora && r.pd_pct != null && empatados.has(r.pd_pct);
            const semMovimento = !fora && r.variacao_pp != null && +r.variacao_pp === 0;
            const naFronteira = !fora && (r.lentes?.agora?.altman?.na_fronteira || r.lentes?.agora?.kanitz?.na_fronteira);
            const abrir = fora ? null : onOpen;
            return (
              <tr key={r.cnpj} onClick={() => abrir && abrir(r.cnpj)}
                title={fora ? 'Esta companhia não está no seu pacote. A ficha abre quando ela entra na sua carteira.' : undefined}
                style={{ cursor: abrir ? 'pointer' : 'default', borderBottom: '1px solid #f1f5f9', background: fora ? '#fcfcfd' : undefined }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#f8fafc'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = fora ? '#fcfcfd' : ''; }}>
                <td style={{ padding: '9px 12px' }}>
                  <span style={{ fontWeight: 600 }}>{r.denom || '—'}</span>
                  {r.ticker && <span style={{ color: C.muted, marginLeft: 6, fontSize: 11.5 }}>{r.ticker}</span>}
                  {naFronteira && (
                    <div style={{ fontSize: 10.5, color: C.roxo, fontWeight: 600 }}
                      title="Um dos índices está a um passo de cruzar a fronteira da faixa. Um trimestre ruim muda a faixa e move a PD em degrau.">
                      no limite de uma faixa
                    </div>
                  )}
                </td>
                <td style={{ padding: '9px 12px', color: C.muted }}>{r.setor || '—'}</td>
                <td style={{ padding: '9px 12px' }}>
                  <span style={{ ...badge, color: ratingColor(r.rating), borderColor: ratingColor(r.rating) }}>{r.rating || '—'}</span>
                  {r.rating_anterior && r.rating_anterior !== r.rating && (
                    <span style={{ color: C.muted, fontSize: 11, marginLeft: 6 }}>era {r.rating_anterior}</span>
                  )}
                </td>
                <td style={{ padding: '9px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                  {fora ? <ForaDoPacote /> : fmtPct(r.pd_pct)}
                  {empatada && (
                    <div style={{ fontWeight: 400, fontSize: 10.5, color: C.amber2, whiteSpace: 'nowrap' }}
                      title="Outras companhias desta lista leem exatamente a mesma PD. A posição relativa entre elas é arbitrária.">
                      empatada
                    </div>
                  )}
                </td>
                <td style={{ padding: '9px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: C.muted }}>
                  {fora ? '' : fmtPct(r.pd_anterior_pct)}
                </td>
                <td style={{ padding: '9px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: semMovimento || r.variacao_pp == null ? C.muted : r.variacao_pp > 0 ? C.red : C.green }}>
                  {fora ? <Direcao linha={r} /> : fmtPp(r.variacao_pp)}
                </td>
                <td style={{ padding: '9px 12px' }}>
                  {/* A travessia de faixa e a explicacao, e explicacao e produto.
                      O servidor ja manda `lentes.disponivel: false` com motivo
                      `fora_da_carteira`; aqui a frase e escrita por extenso em
                      vez de cair no generico "nao sei dizer", que seria falso —
                      o RiskMind sabe, so nao entrega. */}
                  {fora ? <ForaDoPacote curto /> : <PorQue lentes={r.lentes} />}
                </td>
                {mostrarEvento && (
                  <td style={{ padding: '9px 12px', fontSize: 12 }}>
                    {r.evento_publico ? (
                      <span style={{ color: C.red }}>
                        {TIPO_EVENTO[r.evento_publico.tipo] || r.evento_publico.tipo}
                        {' · '}{fmtData(r.evento_publico.data)}
                        {r.evento_publico.censurado && (
                          <span style={{ color: C.muted }} title="Já estava nessa situação antes da janela de coleta da CVM abrir."> (anterior à janela)</span>
                        )}
                      </span>
                    ) : <span style={{ color: C.muted }}>sem registro</span>}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function OcTriagem({ api, onOpen }) {
  const [pd, setPd] = useState(20);
  const [salto, setSalto] = useState(10);
  const [semAto, setSemAto] = useState(true);
  const [soVirada, setSoVirada] = useState(false);
  /* Qual bloco lidera, e qual pergunta esta ativa. Guardar o ID da pergunta em
     vez de deduzi-lo dos parametros evita destacar um botao que so por
     coincidencia bate com o que o cliente digitou a mao. */
  const [foco, setFoco] = useState('nivel');
  const [pergunta, setPergunta] = useState('nivel20');
  const [q, setQ] = useState('');
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [carregando, setCarregando] = useState(false);
  /* Padrao carteira, igual ao servidor. Padrao aberto e como se vaza por
     engano — e aqui vazaria por um clique que o cliente NAO deu. */
  const [modo, setModo] = useState('carteira');
  /* Ver o comentario igual em OcMovimentacaoRisco: `null` deixa o servidor
     escolher, e e o que faz a tela virar de trimestre sozinha. */
  const [dataBase, setDataBase] = useState(null);
  const [comparacao, setComparacao] = useState(null);

  /* Os cortes sao consulta ao servidor, nao filtro de tela: o denominador tem
     de vir do universo inteiro e nao da pagina. O escopo e o trimestre viajam
     na mesma consulta pela mesma razao — filtrar carteira aqui deixaria a PD
     do universo passar pelo fio, que e exatamente o defeito que a PR #32 veio
     fechar na aba "Todas as companhias". */
  const buscar = useCallback((corte, saltoPp, escopo, db, dbComp) => {
    setCarregando(true); setErr(null);
    const qs = new URLSearchParams({ cortes: String(corte), salto: String(saltoPp), escopo });
    if (db) qs.set('data_base', db);
    if (dbComp) qs.set('data_base_comparacao', dbComp);
    return api('/api/oc/deterioracao?' + qs.toString())
      .then((r) => { setD(r); return r; })
      .catch((e) => { setErr(e); })
      .finally(() => setCarregando(false));
  }, [api]);

  useEffect(() => {
    let vivo = true;
    const t = setTimeout(() => { if (vivo) buscar(pd, salto, modo, dataBase, comparacao); }, 250);
    return () => { vivo = false; clearTimeout(t); };
  }, [pd, salto, modo, dataBase, comparacao, buscar]);

  /* So mexe no que a pergunta nomeia. O parametro que ela nao menciona fica
     onde o cliente deixou. */
  const aplicar = (p) => {
    if (p.pd != null) setPd(p.pd);
    if (p.salto != null) setSalto(p.salto);
    setSemAto(!!p.semAto);
    setSoVirada(!!p.soVirada);
    setFoco(p.foco || 'nivel');
    setPergunta(p.id);
  };
  /* Editar um campo a mao desfaz a pergunta pronta: o botao aceso passaria a
     descrever um estado que nao e mais o dele. */
  const aMao = (fn) => (v) => { setPergunta(null); fn(v); };

  const eventosDisponiveis = !!(d?.eventos && d.eventos.disponivel);
  const podeFiltrarAto = eventosDisponiveis;

  const { nivel, saltoLista } = useMemo(() => {
    const listas = d?.listas || {};
    let nv = null; let st = null;
    for (const [k, v] of Object.entries(listas)) {
      if (k.startsWith('pd_acima_')) nv = v;
      else st = v;
    }
    return { nivel: nv, saltoLista: st };
  }, [d]);

  const preparar = useCallback((bloco) => {
    if (!bloco) return { linhas: [], nDele: 0, g: null };
    const base = (podeFiltrarAto && semAto && Array.isArray(bloco.sem_evento_publico))
      ? bloco.sem_evento_publico : bloco.todos || [];
    const termo = q.trim().toLowerCase();
    let linhas = termo
      ? base.filter((l) => (l.denom || '').toLowerCase().includes(termo) || (l.ticker || '').toLowerCase().includes(termo))
      : base;
    /* "Somente quem cruzou uma fronteira" e uma pergunta sobre a LENTE, e a
       companhia fora do pacote nao tem lente. Ela sai desta lista — nao por
       ser de fora, mas por a pergunta nao ter resposta para ela. A diferenca
       importa: o contador abaixo passa a mostrar so as dele, e isso e correto
       aqui e seria errado nos outros cortes. */
    if (soVirada) linhas = linhas.filter((l) => l.lentes?.disponivel && (l.lentes.viradas || []).length > 0);
    const nDele = linhas.filter((l) => l.fora_da_carteira !== true).length;
    return { linhas, nDele, g: bloco.granularidade };
  }, [q, semAto, podeFiltrarAto, soVirada]);

  const blocoNivel = preparar(nivel);
  const blocoSalto = preparar(saltoLista);

  const empatadosDe = (linhas) => {
    const c = new Map();
    /* `pd_pct != null` ja excluia as mascaradas, mas a intencao fica escrita:
       linha sem numero nao empata com nada — contar todas as mascaradas como
       um empate gigante seria um aviso falso em cima de dado que nem existe. */
    for (const l of linhas) if (l.pd_pct != null && l.fora_da_carteira !== true) c.set(l.pd_pct, (c.get(l.pd_pct) || 0) + 1);
    const s = new Set();
    for (const [v, n] of c) if (n > 1) s.add(v);
    return s;
  };

  if (err) {
    const gate = err.status === 401 || err.status === 403;
    return (
      <div style={errBox}>
        {gate ? 'Esta tela faz parte do Capital Aberto. Se o seu plano inclui e ela não abriu, fale com o suporte.'
          : `Não consegui carregar a triagem: ${err.message}`}
      </div>
    );
  }
  if (!d) return <div style={{ color: C.muted, fontSize: 13, padding: 20 }}>carregando triagem…</div>;
  if (d.erro) return <div style={errBox}>Sem leitura disponível para montar esta tela ({d.erro}). {d.motivo || ''}</div>;

  const universoN = d.universo?.n ?? '—';

  return (
    <div>
      {/* Um dos dois, nunca os dois: o seletor absorveu este conteudo.
          Ver `temSeletorDeTrimestre` em OcEscopoControles.jsx. */}
      {!temSeletorDeTrimestre(d) && <Cabecalho d={d} />}
      <ControleEscopo esc={d.escopo} modo={modo} onModo={setModo} carregando={carregando} />
      <SeletorTrimestres d={d} dataBase={dataBase} onDataBase={setDataBase}
        comparacao={comparacao} onComparacao={setComparacao} carregando={carregando} />
      <AvisoEventos eventos={d.eventos} universoN={universoN} />

      {/* ── perguntas prontas + os parametros ── */}
      <div style={{ ...caixa, borderLeftColor: C.gold }}>
        <div style={{ fontWeight: 700, marginBottom: 8 }}>Que pergunta você quer fazer?</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
          {PERGUNTAS.map((p) => {
            const ativa = pergunta === p.id;
            return (
              <button key={p.id} onClick={() => aplicar(p)}
                style={{
                  padding: '5px 11px', borderRadius: 999, fontSize: 12, cursor: 'pointer',
                  border: `1px solid ${ativa ? C.navy : C.line}`,
                  background: ativa ? C.navy : C.paper,
                  color: ativa ? '#fff' : C.ink,
                }}>
                {p.rotulo}
              </button>
            );
          })}
        </div>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center' }}>
          <label style={{ fontSize: 12.5 }}>
            PD igual ou acima de{' '}
            <input type="number" min="1" max="99" step="1" value={pd} style={inp}
              onChange={(e) => aMao(setPd)(Math.max(1, Math.min(99, Number(e.target.value) || 1)))} />{' %'}
          </label>
          <label style={{ fontSize: 12.5 }}>
            salto de{' '}
            <input type="number" min="1" max="99" step="1" value={salto} style={inp}
              onChange={(e) => aMao(setSalto)(Math.max(1, Math.min(99, Number(e.target.value) || 1)))} />{' p.p. ou mais'}
          </label>
          <label style={{ fontSize: 12.5, cursor: podeFiltrarAto ? 'pointer' : 'not-allowed', color: podeFiltrarAto ? C.ink : C.muted }}
            title={podeFiltrarAto ? undefined : 'A lista de eventos não pôde ser lida; sem ela a tela não pode afirmar quem não pediu recuperação.'}>
            <input type="checkbox" checked={podeFiltrarAto && semAto} disabled={!podeFiltrarAto}
              onChange={(e) => aMao(setSemAto)(e.target.checked)} style={{ marginRight: 6 }} />
            somente sem ato público na CVM
          </label>
          <label style={{ fontSize: 12.5, cursor: 'pointer' }}
            title="Mostra apenas quem cruzou uma fronteira de faixa do Altman ou do Kanitz entre as duas data-base. É a pergunta que tem resposta quando a PD empata.">
            <input type="checkbox" checked={soVirada} onChange={(e) => aMao(setSoVirada)(e.target.checked)} style={{ marginRight: 6 }} />
            somente quem cruzou uma fronteira
          </label>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="buscar empresa ou ticker…"
            style={{ ...inp, width: 200 }} />
          {carregando && <span style={{ fontSize: 12, color: C.muted }}>consultando…</span>}
        </div>
      </div>

      {/* A pergunta escolhida decide QUEM LIDERA. O outro corte continua na tela,
          abaixo e com o titulo mais discreto — esconder dado para simplificar
          seria trocar confusao por omissao. */}
      {[
        {
          id: 'nivel',
          titulo: `PD igual ou acima de ${nivel?.corte_pd_pct ?? pd}%`,
          bloco: blocoNivel,
          rotuloG: `com PD ≥ ${nivel?.corte_pd_pct ?? pd}%`,
          nota: null,
        },
        {
          id: 'salto',
          titulo: `Salto de ${saltoLista?.salto_minimo_pp ?? salto} p.p. ou mais entre as duas data-base`,
          bloco: blocoSalto,
          rotuloG: null,
          nota: (
            <div style={{ ...caixa, borderLeftColor: C.amber2, marginBottom: 10 }}>
              <b>Um salto aqui é quase sempre uma mudança de faixa, não uma piora contínua.</b>{' '}
              A PD muda em degrau quando a companhia troca de faixa numa das leituras estruturais,
              então o tamanho do salto indica qual fronteira foi cruzada — não a intensidade da piora.
            </div>
          ),
        },
      ].sort((x, y) => (x.id === foco ? -1 : y.id === foco ? 1 : 0)).map((sec, i) => (
        <section key={sec.id} style={{ marginBottom: 28, opacity: i === 0 ? 1 : 0.92 }}>
          <h3 style={{ margin: '0 0 8px', fontSize: i === 0 ? 15 : 13.5, color: i === 0 ? C.navy : C.muted }}>
            {sec.titulo}
            {' '}<span style={{ fontSize: 12.5, color: C.muted, fontWeight: 400 }}>
              {/* RECUSA 3: denominador junto, sempre. */}
              {/* RECUSA 3 + RECUSA 6: no modo universo a lista mistura o que e
                  dele com o que nao e, e os dois numeros tem de aparecer. */}
              — <b>{sec.bloco.linhas.length}</b> de {universoN} companhias
              {sec.bloco.nDele != null && sec.bloco.nDele !== sec.bloco.linhas.length && (
                <> · <b>{sec.bloco.nDele}</b> {sec.bloco.nDele === 1 ? 'do seu pacote' : 'do seu pacote'}</>
              )}
              {semAto && podeFiltrarAto && <> sem ato público</>}
              {soVirada && <>, que mudaram de faixa</>}
            </span>
          </h3>
          {sec.nota}
          {sec.rotuloG && <Granularidade g={sec.bloco.g} rotulo={sec.rotuloG} />}
          <Tabela linhas={sec.bloco.linhas} onOpen={onOpen} mostrarEvento={eventosDisponiveis}
            empatados={empatadosDe(sec.bloco.linhas)} />
        </section>
      ))}

      {d.aviso && <div style={{ ...caixa, borderLeftColor: C.muted }}>{d.aviso}</div>}
    </div>
  );
}
